/**
 * 管理后台 · 实时监控与日志查询。
 * 替代老系统 checkserver.jsp / man-main.jsp 与 LogAction / TrafficAction。
 */

import { useCallback, useEffect, useState } from 'react'

import {
  adminApi,
  formatDuration,
  formatTime,
  type LogRow,
  type OnlineInfo,
} from '../../lib/adminApi'
import { RoomFlag, splitRoomID } from '../../lib/protocol'
import { Pager } from './RoomsPanel'

interface Props {
  token: string
  onError: (msg: string) => void
}

export function MonitorPanel({ token, onError }: Props) {
  const [info, setInfo] = useState<OnlineInfo | null>(null)

  // 在线数据每 5 秒刷新一次。老系统是靠管理员手动点「刷新」，
  // 上课高峰期想看实时人数得一直按 F5。
  useEffect(() => {
    let alive = true
    const tick = async () => {
      try {
        const d = await adminApi.online(token)
        if (alive) setInfo(d)
      } catch {
        // 轮询失败不打扰用户，下一轮会自动重试。
      }
    }
    void tick()
    const t = window.setInterval(tick, 5000)
    return () => {
      alive = false
      window.clearInterval(t)
    }
  }, [token])

  const rooms = info?.rooms ?? []

  return (
    <div className="adm-panel">
      <div className="stat-cards">
        <div className="stat-card">
          <span className="stat-num">{info?.totalUsers ?? '—'}</span>
          <span className="stat-label">当前在线人数</span>
        </div>
        <div className="stat-card">
          <span className="stat-num">{rooms.length}</span>
          <span className="stat-label">活动房间数</span>
        </div>
        <div className="stat-card">
          <span className="stat-num">{rooms.filter((r) => isRecording(r.flags)).length}</span>
          <span className="stat-label">正在录制</span>
        </div>
        <div className="stat-card">
          <span className="stat-num small">{info?.version ?? '—'}</span>
          <span className="stat-label">服务版本</span>
        </div>
      </div>

      <div className="adm-section">
        <h3>正在使用的房间</h3>
        {rooms.length === 0 ? (
          <p className="adm-empty">当前没有房间在使用</p>
        ) : (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>房间号</th>
                  <th>名称</th>
                  <th>区域/学校</th>
                  <th>在线</th>
                  <th>状态</th>
                </tr>
              </thead>
              <tbody>
                {rooms.map((r) => {
                  const { region, school, local } = splitRoomID(r.roomId)
                  const f = r.flags
                  return (
                    <tr key={r.roomId}>
                      <td className="mono">{r.roomId}</td>
                      <td>{r.name || '—'}</td>
                      <td className="dim">
                        {region} / {school} / {local}
                      </td>
                      <td>
                        <span className="live-dot">{r.userCount}</span>
                      </td>
                      <td>
                        {(f & RoomFlag.StateRecording) !== 0 && (
                          <em className="tag tag-rec">● 录制中</em>
                        )}
                        {(f & RoomFlag.StateLectureMode) !== 0 && <em className="tag">讲课模式</em>}
                        {(f & RoomFlag.StateAppShare) !== 0 && <em className="tag">共享中</em>}
                        {(f & RoomFlag.StateLocked) !== 0 && <em className="tag tag-warn">已锁定</em>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LogsSection token={token} onError={onError} />
    </div>
  )
}

function isRecording(flags: number): boolean {
  return (flags & RoomFlag.StateRecording) !== 0
}

/** 日志查询。 */
function LogsSection({ token, onError }: { token: string; onError: (m: string) => void }) {
  const [rows, setRows] = useState<LogRow[]>([])
  const [total, setTotal] = useState(0)
  const [kind, setKind] = useState('')
  const [account, setAccount] = useState('')
  const [roomId, setRoomId] = useState('')
  const [offset, setOffset] = useState(0)
  const limit = 20

  const load = useCallback(async () => {
    try {
      const p = await adminApi.queryLogs(token, {
        kind: kind || undefined,
        account: account || undefined,
        roomId: Number(roomId) || undefined,
        limit,
        offset,
      })
      setRows(p.items ?? [])
      setTotal(p.total)
    } catch (e) {
      onError(e instanceof Error ? e.message : '查询日志失败')
    }
  }, [token, kind, account, roomId, offset, onError])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div className="adm-section">
      <h3>日志查询</h3>
      <p className="adm-hint">
        进出房、授课、管理操作三类日志合并按时间排列，不必在三个页面之间来回翻。
      </p>

      <div className="adm-bar">
        <select
          value={kind}
          onChange={(e) => {
            setKind(e.target.value)
            setOffset(0)
          }}
        >
          <option value="">全部类型</option>
          <option value="user">进出房</option>
          <option value="teach">授课</option>
          <option value="man">管理操作</option>
        </select>
        <input
          placeholder="账号"
          value={account}
          onChange={(e) => {
            setAccount(e.target.value)
            setOffset(0)
          }}
        />
        <input
          placeholder="房间号"
          value={roomId}
          inputMode="numeric"
          onChange={(e) => {
            setRoomId(e.target.value)
            setOffset(0)
          }}
        />
        <span className="adm-count">共 {total} 条</span>
      </div>

      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>时间</th>
              <th>类型</th>
              <th>账号</th>
              <th>房间</th>
              <th>动作</th>
              <th>来源 IP</th>
              <th>时长</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l, i) => (
              <tr key={`${l.time}-${l.account}-${i}`}>
                <td className="dim">{formatTime(l.time)}</td>
                <td>
                  <em className={`tag tag-${l.kind}`}>{kindText(l.kind)}</em>
                </td>
                <td className="mono">{l.account}</td>
                <td className="mono dim">{l.roomId || '—'}</td>
                <td>{l.action || '—'}</td>
                <td className="dim mono">{l.ip || '—'}</td>
                <td className="dim">{l.duration ? formatDuration(l.duration) : '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="adm-empty">
                  没有匹配的日志
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pager total={total} limit={limit} offset={offset} onChange={setOffset} />
    </div>
  )
}

function kindText(k: string): string {
  switch (k) {
    case 'user':
      return '进出房'
    case 'teach':
      return '授课'
    case 'man':
      return '管理'
    default:
      return k
  }
}
